import React from 'react';
import { mui } from '@local/finsera-core/src/libs';

import { FinDataGridColumnsDialog } from './columns-dialog';
import { FinDataGridFilterDialog } from './filter-dialog';
import { filterColumnsSx, finDataGridSx } from './styles';

type FinDataGridExtraProps = {
  defaultBackgroundColor?: boolean;
  /**
   * Debounces the external onFilterModelChange handler to avoid heavy re-renders on each keystroke.
   * (Does not change the grid's internal input behavior.)
   */
  onFilterModelChangeDebounceMs?: number;
};

/**
 * A customized DataGrid component with consistent styling and fixed column menu icon alignment
 */
const FinDataGrid = ({
  sx,
  defaultBackgroundColor,
  initialState,
  onFilterModelChangeDebounceMs = 500,
  ...props
}: mui.dataGrid.DataGridPremiumProps & FinDataGridExtraProps) => {
  const debounceTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const debouncedOnFilterModelChange = React.useCallback(
    (...args: Parameters<NonNullable<mui.dataGrid.DataGridPremiumProps['onFilterModelChange']>>) => {
      const handler = props.onFilterModelChange;
      if (!handler) return;

      if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = window.setTimeout(() => {
        handler(...args);
      }, onFilterModelChangeDebounceMs);
    },
    [props.onFilterModelChange, onFilterModelChangeDebounceMs]
  );

  const CustomColumnMenu = React.useCallback((props: any) => {
    return (
      <mui.dataGrid.GridColumnMenu
        {...props}
        slots={{
          columnMenuSortItem: null, // Hides sort by asc/desc
          columnMenuPinningItem: null, // Hides pin to left/right
          columnMenuAggregationItem: null, // Hides aggregation
        }}
      />
    );
  }, []);

  const Sx = React.useMemo(() => {
    let localStyles = finDataGridSx(props);
    // If column menu is enabled, apply the filter columns styles
    if (!props.disableColumnMenu)
      localStyles = {
        ...localStyles,
        ...filterColumnsSx(),
      };
    return { ...localStyles, ...(sx || {}) };
  }, [sx, props]);

  return (
    <mui.dataGrid.DataGridPremium
      sx={Sx}
      initialState={{
        pagination: { paginationModel: { pageSize: 10 } },
        density: 'compact',
        ...(initialState || {}),
      }}
      slots={{
        filterPanel: FinDataGridFilterDialog,
        columnsPanel: FinDataGridColumnsDialog,
        columnMenu: CustomColumnMenu,
      }}
      {...props}
      onFilterModelChange={props.onFilterModelChange ? debouncedOnFilterModelChange : undefined}
    />
  );
};

export default FinDataGrid;
